import { useEffect, useState, useMemo } from 'react';
import { PromptVersionControl } from '@Models/types';
import { useAuth } from '@Context/CognitoAuthProvider';
import { accountsAPI } from '@Lib/axios/account';
import {
  PromptSelector,
  CreatePromptModal,
  ViewPromptModal,
  EditPromptModal,
  DeletePromptModal,
} from './';
import { useGeneratorContext } from '@Context/GeneratorContext';

const PromptCrud = () => {
  const { user, logout } = useAuth();
  const account = useMemo(() => accountsAPI(logout), [logout]);
  const { selectedPrompt, setSelectedPrompt } = useGeneratorContext();

  const [prompts, setPrompts] = useState<PromptVersionControl[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [modalSelectedPrompt, setModalSelectedPrompt] =
    useState<PromptVersionControl | null>(null);

  useEffect(() => {
    const fetchPrompts = async () => {
      if (!user?.profile) {
        return;
      }

      setLoadingPrompts(true);

      try {
        const testEmail = user?.profile?.email;
        if (!testEmail) {
          throw new Error('No user email found');
        }

        let userPrompts = [];
        try {
          userPrompts = await account.getAllUserPrompts();
        } catch {
          userPrompts = [];
        }

        let activeGlobalPrompt = null;
        try {
          activeGlobalPrompt = await account.getCurrentGlobalPrompt();
        } catch {
          activeGlobalPrompt = null;
        }

        const userPromptsArray = Array.isArray(userPrompts) ? userPrompts : [];
        const allPrompts =
          activeGlobalPrompt &&
          activeGlobalPrompt.content !== 'No Global Prompt Yet!'
            ? [...userPromptsArray, activeGlobalPrompt]
            : userPromptsArray;

        setPrompts(allPrompts);
      } catch (error) {
        setPrompts([]);
      } finally {
        setLoadingPrompts(false);
      }
    };

    fetchPrompts();
  }, [user, account]);

  const getNextVersion = (): string => {
    const versions = prompts.map((p) => p.version);
    const numericVersions = versions
      .map((v) => parseFloat(v))
      .filter((v) => !isNaN(v))
      .sort((a, b) => b - a);

    const highestVersion = numericVersions[0] || 1.0;
    return (highestVersion + 0.1).toFixed(1);
  };

  const handlePromptSelect = (prompt: PromptVersionControl) => {
    setSelectedPrompt(prompt);
  };

  const handleCreatePrompt = () => {
    setIsCreateModalOpen(true);
  };

  const handleViewPrompt = (prompt: PromptVersionControl) => {
    setModalSelectedPrompt(prompt);
    setIsViewModalOpen(true);
  };

  const handleEditPrompt = (prompt: PromptVersionControl) => {
    setModalSelectedPrompt(prompt);
    setIsEditModalOpen(true);
  };

  const handleDeletePrompt = (prompt: PromptVersionControl) => {
    setModalSelectedPrompt(prompt);
    setIsDeleteModalOpen(true);
  };

  const handleEditFromView = () => {
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handlePromptSuccess = async () => {
    try {
      const userPrompts = await account.getAllUserPrompts();
      const activeGlobalPrompt = await account.getCurrentGlobalPrompt();

      const userPromptsArray = Array.isArray(userPrompts) ? userPrompts : [];
      const allPrompts =
        activeGlobalPrompt &&
        activeGlobalPrompt.content !== 'No Global Prompt Yet!'
          ? [...userPromptsArray, activeGlobalPrompt]
          : userPromptsArray;

      setPrompts(allPrompts);
    } catch (error) {
      console.error('Error refetching prompts:', error);
    }
  };

  const handleConfirmDelete = async (prompt: PromptVersionControl) => {
    setPrompts((prev) => prev.filter((p) => p.version !== prompt.version));
  };

  return (
    <>
      <PromptSelector
        prompts={prompts}
        selectedPrompt={selectedPrompt}
        loadingPrompts={loadingPrompts}
        onPromptSelect={handlePromptSelect}
        onCreatePrompt={handleCreatePrompt}
        onViewPrompt={handleViewPrompt}
        onEditPrompt={handleEditPrompt}
        onDeletePrompt={handleDeletePrompt}
      />

      <CreatePromptModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handlePromptSuccess}
        getNextVersion={getNextVersion}
      />

      <ViewPromptModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        selectedPrompt={modalSelectedPrompt}
        onEdit={handleEditFromView}
      />

      <EditPromptModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        selectedPrompt={modalSelectedPrompt}
        onSuccess={handlePromptSuccess}
        getNextVersion={getNextVersion}
      />

      <DeletePromptModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        selectedPrompt={modalSelectedPrompt}
        onConfirmDelete={handleConfirmDelete}
      />
    </>
  );
};

export default PromptCrud;
